import { createRouter, createWebHistory } from 'vue-router'
import MainView from '@/components/page/main/MainView.vue'
import i18n, { supportedLocales } from '@/core/i18n'
import PassportDBService from '@/core/PassportDBService'
import { watchEffect } from 'vue'

export type ServiceType = 'city' | 'terminal' | 'history'
export type PinActionType = 'setup' | 'verify'

/**
 * 페이지 추가 시 Header에 만료시간 체크 제외 url에 등록해야 함.
 */
const routes = [
  {
    path: '/:locale(en|kr|jp|zh_cn|zh_hk|th)?',
    children: [
      {
        // 메인 페이지
        path: '',
        name: 'main',
        component: MainView
      },
      {
        path: 'check-wifi',
        name: 'check wifi',
        component: () => import('@/components/page/main/ErrorIPView.vue')
      },
      {
        path: 'wifi',
        name: 'wifi',
        component: () => import('../views/AvailableWIFIView.vue'),
        meta: {
          pageTitle: 'Available Wi-Fi'
        }
      },
      {
        path: 'guide',
        name: 'guide',
        component: () => import('../views/GuideView.vue'),
        meta: {
          pageTitle: 'Guide'
        }
      },
      {
        path: 'tips',
        name: 'tips',
        component: () => import('../components/page/main/TipsSwiper.vue'),
        meta: {
          pageTitle: 'Tips'
        }
      },
      {
        // 개인정보 동의 페이지
        path: ':service(city|terminal|history)/consent/privacy',
        name: 'consent-privacy',
        component: () => import('../components/page/consent/PrivacyConsentPage.vue'),
        meta: {
          pageTitle: 'privacy_consent_page'
        }
      },
      {
        // 여권 스캔 페이지
        path: ':service(city|terminal|history)/passport/scan',
        name: 'passport',
        component: () => import('../components/page/passport/PassportScanPage.vue'),
        meta: {
          pageTitle: 'scan_passport_page'
        }
      },
      {
        // Tesseract 여권 스캔 페이지
        path: ':service(city|terminal|history)/passport/scanner',
        name: 'passport-scanner',
        component: () => import('../components/page/passport/PassportScannerPage.vue'),
        meta: {
          pageTitle: 'scan_passport_page'
        }
      },
      {
        // 여권 정보 확인 페이지
        path: ':service(city|terminal|history)/passport/verify',
        name: 'passport-verify',
        component: () => import('../components/page/passport/PassportVerifyPage.vue'),
        meta: {
          pageTitle: 'confirm_passport_page_1' // router.beforeEach에서 동적 설정
        }
      },
      {
        // 핀번호 등록 페이지
        path: ':service(city|terminal|history)/pin/:pinAction(setup)',
        name: 'pin setup',
        component: () => import('../components/page/pin/PinView.vue'),
        meta: {
          pageTitle: 'pin_setup_page'
        }
      },
      {
        // 핀번호 확인 페이지
        path: ':service(city|terminal|history)/pin/:pinAction(verify)',
        name: 'pin verify',
        component: () => import('../components/page/pin/PinView.vue'),
        meta: {
          pageTitle: 'pin_verify_page'
        }
      },
      {
        // 환급 내역 페이지
        path: 'history',
        name: 'refund history',
        component: () => import('../components/page/history/RefundHistoryPage.vue'),
        meta: {
          pageTitle: 'refund_history_page'
        }
      },
      {
        path: ':service(city|terminal|history)/receipts/gate',
        name: 'terminal receipts gate',
        component: () => import('../components/page/receipts/ServiceGate.vue'),
        meta: {
          pageTitle: 'Claim Tax Refund'
        }
      },
      {
        // 시내/도심: 환급 수단 등록 가능 거래 리스트 페이지
        // 출국항: 반출 가능 거래 리스트 페이지
        path: ':service(city|terminal)/receipts',
        name: 'receipts',
        component: () => import('../components/page/receipts/ReceiptListPage.vue'),
        meta: {
          pageTitle: 'scan_receipt_page_1' // router.beforeEach에서 동적 설정
        }
      },
      {
        // 전표 스캔 페이지
        path: ':service(city|terminal)/receipts/scan',
        name: 'receipt-scan',
        component: () => import('../components/component/ScanerReceipt.vue'),
        meta: {
          pageTitle: 'Claim Tax Refund'
        }
      },
      {
        // 환급 수단 선택 페이지
        path: ':service(city|terminal)/refund/method',
        name: 'refund-method',
        component: () => import('../components/page/refund/RefundMethodPage.vue'),
        meta: {
          pageTitle: 'refund_method_page'
        }
      },
      {
        // 알리페이 환급 등록 페이지
        path: ':service(city|terminal)/refund/alipay',
        name: 'alipay-refund',
        component: () => import('../components/page/refund/AlipayRefundPage.vue'),
        meta: {
          pageTitle: 'refund_method_page'
        }
      },
      {
        // 카드 환급 등록 페이지
        path: ':service(city|terminal)/refund/card',
        name: 'card-refund',
        component: () => import('../components/page/refund/CardRefundPage.vue'),
        meta: {
          pageTitle: 'refund_method_page'
        }
      },
      {
        path: ':service(city|terminal)/refund/method/history',
        name: 'refund-method-history',
        component: () => import('../components/page/refund/RefundMethodHistoryPage.vue'),
        meta: {
          pageTitle: 'refund_history_page'
        }
      },
      {
        path: ':service(city)/done',
        name: 'city-done',
        component: () => import('../components/page/done/DoneCityPage.vue'),
        meta: {
          pageTitle: 'done_city_page'
        }
      },
      {
        path: ':service(terminal)/done',
        name: 'terminal-done',
        component: () => import('../components/page/done/DoneTerminalPage.vue'),
        meta: {
          pageTitle: 'done_terminal_page'
        }
      }
    ]
  },
  {
    path: '/404',
    name: 'error page',
    component: () => import('@/views/Error404View.vue')
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  },
  {
    path: '/test',
    name: 'test page',
    component: () => import('@/components/page/test/test-index.vue')
  },
  {
    path: '/test/mrz',
    name: 'mrz test page',
    component: () => import('@/components/page/test/mrz-test.vue')
  },
  {
    path: '/test/zxing-test',
    name: 'zxing test page',
    component: () => import('@/components/page/test/zxing-test.vue')
  },
  {
    path: '/test/barcode-scanner',
    name: 'barcode scanner test',
    component: () => import('@/components/page/test/barcode-scanner.vue')
  },
  {
    path: '/test/header-test',
    name: 'fixed header test',
    component: () => import('@/components/page/test/header-test.vue')
  },
  {
    path: '/test/camera-test',
    name: 'camera test',
    component: () => import('@/components/page/test/camera-test.vue')
  },
  {
    path: '/test/ocr-test',
    name: 'ocr test',
    component: () => import('@/components/page/test/ocr-test-2.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  document.documentElement.style.scrollBehavior = 'initial'

  /* 언어 설정 - start */
  // URL로 부터 언어 설정 값 추출
  let locale = to.params.locale as string

  // 언어 설정이 없을 경우 영어(en) 으로 통일
  if (supportedLocales.findIndex(localeMap => localeMap.code === locale) === -1) {
    const browserLang = navigator.language ?? 'en'
    if (browserLang.match(/ko|kr/i)) {
      locale = 'kr'
    } else if (browserLang.match(/zh|cn|hans/i)) {
      locale = 'zh_cn'
    } else if (browserLang.match(/zh|hk|hant/i)) {
      locale = 'zh_hk'
    } else if (browserLang.match(/jp/i)) {
      locale = 'jp'
    } else if (browserLang.match(/th/i)) {
      locale = 'th'
    } else {
      locale = 'en'
    }
  }

  // i18n 전역으로 설정
  if (i18n.global.locale.value !== locale) {
    i18n.global.locale.value = locale as any
  }
  /* 언어 설정 - end */

  /* 동적 페이지 제목 설정 - start */
  if (to.name === 'passport-verify') {
    await PassportDBService.getInstance().findFirst()
      .then(p => {
        if (p) {
          to.meta.pageTitle = 'confirm_passport_page_2'
        }
      })
  } else if (to.name === 'receipts') {
    to.meta.pageTitle = to.fullPath.includes('city') ? 'scan_receipt_page_1' : 'scan_receipt_page_2'
  }
  /* 동적 페이지 제목 설정 - end */

  /* 쿼리파라미터 sectiontype 유지 설정 - start */
  // sectiontype이 존재할 경우 쿼리 스트링 유지
  const { sectiontype } = from.query
  const mergedQuery = { ...to.query }

  if (sectiontype && !mergedQuery.sectiontype) {
    mergedQuery.sectiontype = sectiontype
  }
  if (JSON.stringify(mergedQuery) !== JSON.stringify(to.query)) {
    next({
      path: to.path,
      query: mergedQuery
    })
  }
  /* 쿼리파라미터 sectiontype 유지 설정 - end */

  next()
})

router.onError((err, to) => {
  const msg = (err as Error).message || ''
  if (err.name === 'ChunkLoadError' || /loading chunk/i.test(msg)) {
    window.location.reload()
  }
})

watchEffect(() => {
  const lang = i18n.global.locale.value
  document.body.lang = lang
})

export default router
